import { object, number, string, mixed, lazy, date } from 'yup';

import { DATE_RELATIONS } from '@frontend/api';

import {
  valueUsesAmountAndUnitInput,
  valueUsesDateRangeInput,
  valueUsesSetUnitInput,
  valueUsesSingleDateInput,
} from './components/DateRelationFilter/DateRelationFilter.util';
import type {
  AmountAndUnitRelationFilterValue,
  ExcludableDateRelation,
  RelationFilterValue,
  DateRangeRelationFilterValue,
  SingleDateRelationFilterValue,
  TransformedAmountAndUnitValue,
  TransformedDateRangeValue,
  TransformedRelationFilterValue,
  TransformedSetUnitValue,
  TransformedSingleDateValue,
  SetUnitRelationFilterValue,
} from './components/DateRelationFilter/types';

export const transformAmountAndUnitValue = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  value: AmountAndUnitRelationFilterValue<ExcludedRelations>,
): TransformedAmountAndUnitValue<ExcludedRelations> => {
  const { relation, amount, ...restValue } = value;
  let updatedRelation = relation;
  if (relation === DATE_RELATIONS.IN_THE_NEXT) {
    updatedRelation = DATE_RELATIONS.NEXT;
  } else if (relation === DATE_RELATIONS.IN_THE_LAST) {
    updatedRelation = DATE_RELATIONS.PREVIOUS;
  }
  return { ...restValue, relation: updatedRelation, amount: amount };
};

export const transformDateRangeValue = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  value: DateRangeRelationFilterValue<ExcludedRelations>,
): TransformedDateRangeValue<ExcludedRelations> =>
  value.startDate !== null && value.endDate !== null ? value : undefined;

export const transformSingleDateValue = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  value: SingleDateRelationFilterValue<ExcludedRelations>,
): TransformedSingleDateValue<ExcludedRelations> =>
  value.startDate !== null || value.relativeDate !== ''
    ? {
        relation: value.relation,
        startDate: value.startDate,
        relativeDate: value.relativeDate,
      }
    : undefined;

export const transformSetUnitValue = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  value: SetUnitRelationFilterValue<ExcludedRelations>,
): TransformedSetUnitValue<ExcludedRelations> => {
  const { relation, ...restValue } = value;
  return { ...restValue, relation: DATE_RELATIONS.CURRENT };
};

/**
 * Set date relation filters to undefined if they have null date values, and
 * transform values to match shape expected by backend.
 */
export const transformRelationFilterValue = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  value?: RelationFilterValue<ExcludedRelations>,
): TransformedRelationFilterValue<ExcludedRelations> => {
  if (value === undefined) {
    return undefined;
  }

  if (valueUsesAmountAndUnitInput(value)) {
    return transformAmountAndUnitValue(value);
  }

  if (valueUsesSingleDateInput(value)) {
    return transformSingleDateValue(value);
  }

  if (valueUsesDateRangeInput(value)) {
    return transformDateRangeValue(value);
  }

  if (valueUsesSetUnitInput(value)) {
    return transformSetUnitValue(value);
  }

  return value;
};

export const getRelativeDateFilterValidationSchema = <
  ExcludedRelations extends ExcludableDateRelation,
>() =>
  lazy((value: RelationFilterValue<ExcludedRelations>) => {
    return value
      ? object().shape({
          relation: string().required('Relation is required'),
          startDate: date().nullable(),
          endDate: date().nullable(),
          unit: string().when('relation', {
            is: (relationValue: (typeof value)['relation']) =>
              relationValue === DATE_RELATIONS.CURRENT ||
              relationValue === DATE_RELATIONS.PREVIOUS ||
              relationValue === DATE_RELATIONS.NEXT,
            then: string().required('Date unit is required'),
            otherwise: string().notRequired(),
          }),
          amount: mixed().when('relation', {
            is: (relationValue: (typeof value)['relation']) =>
              relationValue === DATE_RELATIONS.PREVIOUS ||
              relationValue === DATE_RELATIONS.NEXT,
            then: number()
              .nullable(false)
              .moreThan(0, 'Value must be a positive number')
              .required('Value must be a positive number'),
            otherwise: mixed().notRequired(),
          }),
        })
      : mixed().notRequired();
  });

export const getPaginationCurrentPage = (pageParam?: string) => {
  const rawPageNumber = Number(pageParam);
  const rawPageNumberIsPositiveInteger =
    !isNaN(rawPageNumber) &&
    rawPageNumber >= 1 &&
    Number.isInteger(rawPageNumber);
  return rawPageNumberIsPositiveInteger ? rawPageNumber : 1;
};
